// src/screens/cadastro/Register.js

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../../firebaseconfig";
import { Eye, EyeOff } from "lucide-react";
import "./Register.css";

// Funções do Firebase
async function salvarDados(authObj, cpf, nomeCompleto) {
  try {
    const usuarioRef = doc(db, "usuarios", authObj.user.uid);
    await setDoc(
      usuarioRef,
      {
        uid: authObj.user.uid,
        email: authObj.user.email,
        cpf: cpf,
        nomeCompleto: nomeCompleto,
        premium: false,
        customerEmail: authObj.user.email,
        calcleite: false,
        calccafe: false,
        calcbovCria: false,
        calcbovRecria: false,
        calcbovEngorda: false,
        calcagro: false,
        dataCadastro: serverTimestamp(),
        ultimaAtualizacao: serverTimestamp(),
        emailConfirmado: false, // Começa como 'false'
      },
      { merge: true }
    );
  } catch (e) {
    console.error("Erro no salvamento de dados de usuário:", e);
    throw new Error("Erro ao salvar dados do usuário");
  }
}

function errosFirebase(error) {
  switch (error.code) {
    case "auth/email-already-in-use": return "Este e-mail já está em uso.";
    case "auth/invalid-email": return "O e-mail fornecido é inválido.";
    case "auth/weak-password": return "A senha é muito fraca. Use uma senha mais forte.";
    default: return "Erro ao cadastrar. Tente novamente.";
  }
}

async function cadastrar(email, senha, cpf, nomeCompleto) {
  try {
    const cpfNumerico = cpf.replace(/[^\d]/g, "");
    const dadosDoUsuario = await createUserWithEmailAndPassword(auth, email, senha);
    await salvarDados(dadosDoUsuario, cpfNumerico, nomeCompleto);
    return "sucesso";
  } catch (error) {
    console.error("Erro no cadastro:", error);
    return errosFirebase(error);
  }
}

const Register = () => {
  const [nomeCompleto, setNomeCompleto] = useState("");
  const [email, setEmail] = useState("");
  const [cpf, setCpf] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const validarCPF = (cpf) => {
    cpf = cpf.replace(/[^\d]/g, "");
    if (cpf.length !== 11) return false;
    if (/^(\d)\1{10}$/.test(cpf)) return false;
    let soma = 0;
    for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
    let resto = 11 - (soma % 11);
    let digito1 = resto === 10 || resto === 11 ? 0 : resto;
    if (digito1 !== parseInt(cpf.charAt(9))) return false;
    soma = 0;
    for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
    resto = 11 - (soma % 11);
    let digito2 = resto === 10 || resto === 11 ? 0 : resto;
    return digito2 === parseInt(cpf.charAt(10));
  };

  const formatarCPF = (text) => {
    const numericValue = text.replace(/[^\d]/g, "").slice(0, 11);
    if (numericValue.length <= 3) return numericValue;
    if (numericValue.length <= 6) return `${numericValue.slice(0, 3)}.${numericValue.slice(3)}`;
    if (numericValue.length <= 9) return `${numericValue.slice(0, 3)}.${numericValue.slice(3, 6)}.${numericValue.slice(6)}`;
    return `${numericValue.slice(0, 3)}.${numericValue.slice(3, 6)}.${numericValue.slice(6, 9)}-${numericValue.slice(9, 11)}`;
  };

  const handleCpfChange = (e) => { setCpf(formatarCPF(e.target.value)); };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    if (!agreedToTerms) {
      setError("Você deve concordar com a Política de Privacidade para continuar.");
      return;
    }
    if (!nomeCompleto.trim()) {
      setError("Por favor, insira seu nome completo.");
      return;
    }
    const cpfNumerico = cpf.replace(/[^\d]/g, "");
    if (!validarCPF(cpfNumerico)) {
      setError("Por favor, insira um CPF válido.");
      return;
    }
    if (password.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      setError("As senhas não coincidem.");
      return;
    }

    setIsLoading(true);
    try {
      const resultado = await cadastrar(email.trim(), password, cpf, nomeCompleto.trim());

      if (resultado === "sucesso" && auth.currentUser) {
        await sendEmailVerification(auth.currentUser);
        
        // --- ALTERAÇÃO PRINCIPAL ---
        // Redireciona para a tela de verificação, passando o e-mail
        navigate("/verificar-email", { state: { email: email.trim() } });
      } else {
        setError(resultado);
        setIsLoading(false); // Libera o botão em caso de erro
      }
    } catch (err) {
      setError("Ocorreu um erro inesperado. Tente novamente.");
      console.error("Erro no handleRegister:", err);
      setIsLoading(false);
    }
  };

  return (
    <div className="agroauth-register-container">
      <div className="agroauth-register-card">
        <div className="agroauth-register-header">
          <h2 className="agroauth-register-title">Crie sua conta</h2>
          <p className="agroauth-register-subtitle">
            Preencha os dados para se cadastrar na plataforma
          </p>
        </div>

        <form onSubmit={handleRegister} className="agroauth-register-form">
          {error && <div className="agroauth-error-message">{error}</div>}
          
          <div className="agroauth-input-group">
            <label htmlFor="agroauth-name" className="agroauth-input-label"> Nome Completo </label>
            <input id="agroauth-name" type="text" value={nomeCompleto} onChange={(e) => setNomeCompleto(e.target.value)} placeholder="Digite seu nome completo" className="agroauth-input-field" required />
          </div>
          <div className="agroauth-input-group">
            <label htmlFor="agroauth-email" className="agroauth-input-label"> Email </label>
            <input id="agroauth-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="seu@email.com" className="agroauth-input-field" required />
          </div>
          <div className="agroauth-input-group">
            <label htmlFor="agroauth-cpf" className="agroauth-input-label"> CPF </label>
            <input id="agroauth-cpf" type="text" value={cpf} onChange={handleCpfChange} placeholder="000.000.000-00" className="agroauth-input-field" maxLength={14} required />
          </div>
          <div className="agroauth-input-group">
            <label htmlFor="agroauth-password" className="agroauth-input-label"> Senha </label>
            <div className="agroauth-password-wrapper">
              <input id="agroauth-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Mínimo 6 caracteres" className="agroauth-input-field" required />
              <button type="button" className="agroauth-password-toggle" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? (<EyeOff size={20} color="#718096" />) : (<Eye size={20} color="#718096" />)}
              </button>
            </div>
          </div>
          <div className="agroauth-input-group">
            <label htmlFor="agroauth-confirm-password" className="agroauth-input-label"> Confirmar Senha </label>
            <div className="agroauth-password-wrapper">
              <input id="agroauth-confirm-password" type={showConfirmPassword ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Digite a senha novamente" className="agroauth-input-field" required />
              <button type="button" className="agroauth-password-toggle" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                {showConfirmPassword ? (<EyeOff size={20} color="#718096" />) : (<Eye size={20} color="#718096" />)}
              </button>
            </div>
          </div>
          
          <div className="agroauth-terms-group">
            <input
              id="agroauth-terms"
              type="checkbox"
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              className="agroauth-terms-checkbox"
            />
            <label htmlFor="agroauth-terms" className="agroauth-terms-label">
              Eu li e concordo com a{" "}
              <a href="/politica-de-privacidade" target="_blank" rel="noopener noreferrer">
                Política de Privacidade
              </a>
              .
            </label>
          </div>

          <button
            type="submit"
            className="agroauth-register-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="agroauth-spinner"></span>
            ) : (
              "Criar minha conta"
            )}
          </button>
        </form>

        <div className="agroauth-register-footer">
          <p className="agroauth-login-prompt-text">
            Já possui uma conta?{" "}
            <Link to="/login" className="agroauth-login-prompt-link">
              Acesse aqui
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;